import React, { useCallback } from 'react';
import { useFormStore } from 'simple-creator/stores/formStore';
import { useBalloonEventsMetadata } from 'react-ui';
import { Language, CategoryModel } from 'ts-core';
import { CategorySelector } from '../inputs/CategorySelector';
import { RichTextEditor } from '../inputs/RichTextEditor';

export const BasicInfoStep: React.FC = () => {
  const { formData, updateField, touchField } = useFormStore();
  const { metadata } = useBalloonEventsMetadata();

  // Helper to find category by slug, including subcategories (recursive search)
  const findCategoryBySlug = useCallback((slug: string): CategoryModel | null => {
    if (!metadata || !slug) return null;
    
    const searchInCategories = (categories: CategoryModel[]): CategoryModel | null => {
      if (!categories) return null;
      for (const category of categories) {
        if (!category || !category.slug) continue;
        if (category.slug.toLowerCase() === slug.toLowerCase()) {
          return category;
        }
        // Search in children
        if (category.children && category.children.length > 0) {
          const found = searchInCategories(category.children);
          if (found) return found;
        }
      }
      return null;
    };
    
    const allCategories = metadata.getAllCategories();
    return allCategories ? searchInCategories(allCategories) : null;
  }, [metadata]);

  return (
    <div className="space-y-6">
      {/* Title Input */}
      <div>
        <label className="label block mb-2">
          Event Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => updateField('title', e.target.value)}
          onBlur={() => touchField('title')}
          placeholder="e.g., Summer Music Festival"
          className="input"
          maxLength={100}
        />
        <p className="text-sm text-gray-500 mt-1">
          {formData.title.length}/100 characters
        </p>
      </div>

      {/* Category Selection */}
      <div>
        <label className="label block mb-2">
          Category <span className="text-red-500">*</span>
        </label>
        <p className="text-sm text-gray-500 mb-3">
          Select one or more categories that best describe your event
        </p>
        <CategorySelector
          selected={formData.categories}
          onChange={(categories) => updateField('categories', categories)}
          multiple={true}
        />
      </div>

      {/* Description */}
      <div>
        <label className="label block mb-2">
          Description <span className="text-red-500">*</span>
        </label>
        <RichTextEditor
          value={formData.description || ''}
          onChange={(html) => updateField('description', html)}
          placeholder="Tell people what your event is about..."
          maxLength={5000}
          minHeight="120px"
        />
      </div>

      {/* Live Preview */}
      <div className="mt-8 p-4 bg-gray-50 rounded-lg">
        <p className="text-sm text-gray-600 mb-2">Preview:</p>
        <div className="preview-card p-4">
          <h3 className="font-heading font-semibold text-xl">
            {formData.title || 'Event Title'}
          </h3>
          {formData.description && (
            <div 
              className="text-gray-600 text-sm mt-2 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: formData.description }}
            />
          )}
          {formData.categories.length > 0 && metadata && (
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.categories.map((slug) => {
                const category = findCategoryBySlug(slug);
                if (!category) return null;
                
                const displayName = category.displayName.getText(Language.en);
                
                return (
                  <span
                    key={slug}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: category.color,
                      color: '#FFFFFF',
                    }}
                  >
                    <span
                      dangerouslySetInnerHTML={{__html: category.icon}}
                      style={{
                        fontFamily: 'fontAwesome',
                      }}
                    />
                    <span>{displayName}</span>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
